import { computed, ref, type Ref } from "vue";
import { ReportedInvoiceItemFormInterface } from "@/modules/invoices/types";
import {
  normalizeReportedAmountInputValue,
  sanitizeReportedAmountInput,
} from "@/modules/invoices/_utils/reported-invoice-items";

export const useReportedInvoiceRows = ({
  rows,
  rowKeyPrefix = "reported-row",
  defaultCurrency,
  onRowsEdited,
}: {
  rows: Ref<Array<ReportedInvoiceItemFormInterface>>;
  rowKeyPrefix?: string;
  defaultCurrency?: Ref<string>;
  onRowsEdited?: () => void;
}) => {
  const reportedRowKeyIndex = ref<number>(0);
  const reportedAmountDrafts = ref<Record<string, string>>({});
  const resolvedDefaultCurrency = computed(() => defaultCurrency?.value || "SEK");

  const markRowsEdited = (): void => {
    onRowsEdited?.();
  };

  const nextReportedRowKey = (): string => {
    reportedRowKeyIndex.value += 1;
    return `${rowKeyPrefix}-${reportedRowKeyIndex.value}`;
  };

  const ensureReportedRowKey = (row: ReportedInvoiceItemFormInterface): string => {
    if (!row.local_key) {
      row.local_key = nextReportedRowKey();
    }
    return row.local_key;
  };

  const withReportedRowKey = (
    item: ReportedInvoiceItemFormInterface
  ): ReportedInvoiceItemFormInterface => ({
    ...item,
    local_key: item.local_key || (item.id ? `${rowKeyPrefix}-id-${item.id}` : nextReportedRowKey()),
  });

  const resetReportedAmountDrafts = (): void => {
    reportedAmountDrafts.value = {};
  };

  const handleAddManualReportedRow = (): void => {
    markRowsEdited();
    rows.value = [
      ...rows.value,
      {
        local_key: nextReportedRowKey(),
        kind: "manual_adjustment",
        description: "",
        amount_currency: rows.value[0]?.amount_currency || resolvedDefaultCurrency.value,
        amount_major: "0.00",
      },
    ];
  };

  const onReportedAmountInput = (
    row: ReportedInvoiceItemFormInterface,
    value: string | undefined
  ): void => {
    markRowsEdited();
    const localKey = ensureReportedRowKey(row);
    reportedAmountDrafts.value = {
      ...reportedAmountDrafts.value,
      [localKey]: sanitizeReportedAmountInput(value),
    };
  };

  const getReportedAmountInputValue = (row: ReportedInvoiceItemFormInterface): string => {
    const localKey = ensureReportedRowKey(row);
    if (reportedAmountDrafts.value[localKey] !== undefined) {
      return reportedAmountDrafts.value[localKey];
    }
    return normalizeReportedAmountInputValue(row.amount_major);
  };

  const commitReportedAmount = (
    row: ReportedInvoiceItemFormInterface,
    value: string | undefined
  ): void => {
    markRowsEdited();
    const localKey = ensureReportedRowKey(row);
    const nextValue = sanitizeReportedAmountInput(
      value !== undefined ? value : reportedAmountDrafts.value[localKey]
    );
    row.amount_major = nextValue || "0";
    const nextDrafts = { ...reportedAmountDrafts.value };
    delete nextDrafts[localKey];
    reportedAmountDrafts.value = nextDrafts;
  };

  const handleRemoveReportedRow = (localKey: string): void => {
    markRowsEdited();
    if (reportedAmountDrafts.value[localKey] !== undefined) {
      const nextDrafts = { ...reportedAmountDrafts.value };
      delete nextDrafts[localKey];
      reportedAmountDrafts.value = nextDrafts;
    }
    rows.value = rows.value.filter(
      (row: ReportedInvoiceItemFormInterface) => row.local_key !== localKey
    );
  };

  return {
    withReportedRowKey,
    resetReportedAmountDrafts,
    handleAddManualReportedRow,
    onReportedAmountInput,
    getReportedAmountInputValue,
    commitReportedAmount,
    handleRemoveReportedRow,
  };
};
